import { Button, Group, Modal, Select, SimpleGrid, Stack, TextInput } from "@mantine/core";
import { type ChangeEvent, useEffect, useState } from "react";
import type { MessageKey } from "../../../shared/i18n";
import type { SectionKey } from "../../api/client";
import { useNotifyError } from "../../api/queries";
import { MonthInput } from "../../components/month-input";
import { useMessages } from "../../i18n/provider";

const TITLES: Record<SectionKey, MessageKey> = {
  experience: "editor.addExperience",
  education: "editor.addEducation",
  projects: "editor.addProject",
  skills: "editor.addSkill",
  languages: "editor.addLanguage",
  certifications: "editor.addCertification",
  awards: "editor.addAward",
  links: "editor.addLink",
};

const REQUIRED: Record<SectionKey, string[]> = {
  experience: ["company", "role"],
  education: ["institution"],
  projects: ["name"],
  skills: ["name"],
  languages: ["name"],
  certifications: ["name"],
  awards: ["title"],
  links: ["label", "url"],
};

export function AddItemDialog({
  section,
  opened,
  onClose,
  onSubmit,
}: {
  section: SectionKey;
  opened: boolean;
  onClose: () => void;
  onSubmit: (input: Record<string, unknown>) => Promise<void>;
}) {
  const t = useMessages();
  const notifyError = useNotifyError();
  const [values, setValues] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (opened) {
      setValues(
        section === "languages"
          ? { proficiency: "fluent" }
          : section === "links"
            ? { type: "website" }
            : {},
      );
    }
  }, [opened, section]);

  const set = (key: string) => (event: ChangeEvent<HTMLInputElement>) =>
    setValues((current) => ({ ...current, [key]: event.currentTarget.value }));

  const value = (key: string) => values[key] ?? "";
  const month = (key: string) => value(key) || null;
  const optional = (key: string) => value(key).trim() || null;

  const valid = REQUIRED[section].every((key) => value(key).trim().length > 0);

  const buildInput = (): Record<string, unknown> => {
    switch (section) {
      case "experience":
        return {
          company: value("company").trim(),
          role: value("role").trim(),
          startDate: month("startDate"),
        };
      case "education":
        return {
          institution: value("institution").trim(),
          startDate: month("startDate"),
        };
      case "projects":
        return { name: value("name").trim() };
      case "skills":
        return { name: value("name").trim() };
      case "languages":
        return {
          name: value("name").trim(),
          proficiency: value("proficiency") || "fluent",
        };
      case "certifications":
        return {
          name: value("name").trim(),
          issuer: optional("issuer"),
        };
      case "awards":
        return {
          title: value("title").trim(),
          issuer: optional("issuer"),
        };
      case "links":
        return {
          type: value("type") || "website",
          label: value("label").trim(),
          url: value("url").trim(),
        };
    }
  };

  const submit = async () => {
    setBusy(true);
    try {
      await onSubmit(buildInput());
      onClose();
    } catch (error) {
      notifyError(error);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal opened={opened} onClose={onClose} title={t(TITLES[section])} size="md">
      <Stack gap="sm">
        {section === "experience" ? (
          <>
            <TextInput
              label={t("experience.company")}
              value={value("company")}
              onChange={set("company")}
              data-autofocus
            />
            <TextInput label={t("experience.role")} value={value("role")} onChange={set("role")} />
            <MonthInput
              label={t("experience.startDate")}
              value={value("startDate")}
              onChange={set("startDate")}
            />
          </>
        ) : null}

        {section === "education" ? (
          <>
            <TextInput
              label={t("education.institution")}
              value={value("institution")}
              onChange={set("institution")}
              data-autofocus
            />
            <MonthInput
              label={t("education.startDate")}
              value={value("startDate")}
              onChange={set("startDate")}
            />
          </>
        ) : null}

        {section === "projects" ? (
          <TextInput
            label={t("projects.name")}
            value={value("name")}
            onChange={set("name")}
            data-autofocus
          />
        ) : null}

        {section === "skills" ? (
          <TextInput
            label={t("skills.name")}
            value={value("name")}
            onChange={set("name")}
            data-autofocus
          />
        ) : null}

        {section === "languages" ? (
          <SimpleGrid cols={{ base: 1, sm: 2 }}>
            <TextInput
              label={t("languages.name")}
              value={value("name")}
              onChange={set("name")}
              data-autofocus
            />
            <Select
              label={t("languages.proficiency")}
              value={value("proficiency")}
              onChange={(next) => setValues((current) => ({ ...current, proficiency: next ?? "" }))}
              allowDeselect={false}
              data={[
                { value: "native", label: t("languages.native") },
                { value: "fluent", label: t("languages.fluent") },
                { value: "professional", label: t("languages.professional") },
                { value: "limited", label: t("languages.limited") },
              ]}
            />
          </SimpleGrid>
        ) : null}

        {section === "certifications" ? (
          <>
            <TextInput
              label={t("certifications.name")}
              value={value("name")}
              onChange={set("name")}
              data-autofocus
            />
            <TextInput
              label={t("certifications.issuer")}
              value={value("issuer")}
              onChange={set("issuer")}
            />
          </>
        ) : null}

        {section === "awards" ? (
          <>
            <TextInput
              label={t("awards.title")}
              value={value("title")}
              onChange={set("title")}
              data-autofocus
            />
            <TextInput
              label={t("awards.issuer")}
              value={value("issuer")}
              onChange={set("issuer")}
            />
          </>
        ) : null}

        {section === "links" ? (
          <SimpleGrid cols={{ base: 1, sm: 2 }}>
            <Select
              label={t("links.type")}
              value={value("type")}
              onChange={(next) => setValues((current) => ({ ...current, type: next ?? "custom" }))}
              allowDeselect={false}
              data={[
                { value: "linkedin", label: t("links.linkedin") },
                { value: "github", label: t("links.github") },
                { value: "website", label: t("links.website") },
                { value: "portfolio", label: t("links.portfolio") },
                { value: "email", label: t("links.email") },
                { value: "custom", label: t("links.custom") },
              ]}
            />
            <TextInput label={t("links.label")} value={value("label")} onChange={set("label")} />
            <TextInput
              label={t("links.url")}
              value={value("url")}
              onChange={set("url")}
              style={{ gridColumn: "1 / -1" }}
            />
          </SimpleGrid>
        ) : null}

        <Group justify="flex-end" mt="sm">
          <Button variant="subtle" color="gray" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button disabled={!valid} loading={busy} onClick={() => void submit()}>
            {t("common.add")}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
